import { OpenPanel, type TrackProperties } from '@openpanel/web';
import config from '../config';

const clientId = config.OPENPANEL_CLIENT_ID || undefined;
const apiUrl = config.OPENPANEL_API_URL || undefined;

// ─── Consent gate (task 461) ─────────────────────────────────────────────────

/**
 * Whether the signed-in User has opted OUT of `analytics_tracking` in the
 * preference center. Default false (= opted in): the analytics category uses
 * the opt-out model per the approved consent report, so absent ledger events
 * mean tracking is on. Set from the AMS-backed preference state after auth
 * init, and flipped live when the preference-center toggle changes.
 */
let analyticsOptedOut = false;

export const op = new OpenPanel(
  clientId
    ? {
        clientId,
        apiUrl,
        trackScreenViews: true,
        trackOutgoingLinks: true,
        trackAttributes: true,
        // The consent gate: every SDK send — auto screen views, outgoing
        // links, data-track attributes, identify, and explicit track() —
        // passes through this filter, so flipping the opt-out silences the
        // client immediately without tearing the instance down.
        filter: () => !analyticsOptedOut,
      }
    : {
        clientId: 'disabled',
        apiUrl: undefined,
        // Belt-and-braces: without `disabled` the SDK would fall back to the
        // public OpenPanel ingest URL for any explicit track() call.
        disabled: true,
        trackScreenViews: false,
        trackOutgoingLinks: false,
        trackAttributes: false,
      }
);

/**
 * Returns true when OpenPanel is active: clientId is configured AND the User
 * has not opted out of analytics tracking.
 */
export const isAnalyticsEnabled = (): boolean =>
  Boolean(clientId) && !analyticsOptedOut;

/**
 * Apply the User's `analytics_tracking` preference to the client, in-session.
 * Opting out swaps the app to the no-op behavior (the SDK filter blocks auto
 * screen views; `isAnalyticsEnabled()` blocks identify and custom events).
 * Opting back in resumes tracking immediately and re-applies the last known
 * identity so the profile re-attaches without waiting for the next auth
 * refresh.
 */
export function setAnalyticsConsent(optedIn: boolean): void {
  const wasOptedOut = analyticsOptedOut;
  analyticsOptedOut = !optedIn;

  if (!optedIn) {
    // Forget the applied-identity signature so a later opt-in re-identifies.
    lastIdentitySignature = null;
    return;
  }

  if (wasOptedOut && lastIdentity) {
    applyAnalyticsIdentity(lastIdentity);
  }
}

// ─── Identity and global properties (task 426) ────────────────────────────────

/**
 * Value of the `app` global property. Distinguishes this app's events from
 * Monarch Admin's (`monarch-admin`) inside OpenPanel.
 */
const APP_NAME = 'innovate-app';

/**
 * Who the signed-in user is, analytically speaking. `internal` = Monarch staff
 * (the token carries platform-scope roles); everyone else is `customer`.
 */
export type AnalyticsAudience = 'customer' | 'internal';

/**
 * Identity inputs, all sourced from the decoded Keycloak access token and the
 * AMS claims it carries.
 *
 * Personal data (email, name) travels ONLY as OpenPanel profile traits on the
 * identify call, never as event properties or global properties (task 1296,
 * revising the August "no personal data in properties" rule per
 * `project/reports/platform-openpanel-marketing-split-and-session-identity.md`).
 * The Keycloak `sub` stays the profile id.
 */
export interface AnalyticsIdentity {
  /** Keycloak `sub` claim — the OpenPanel profile ID. */
  profileId: string;
  /** AMS `account_id` claim; absent for an unprovisioned user. */
  accountId?: string;
  /** AMS `plan` claim (plan slug, e.g. `free`). */
  plan?: string;
  /**
   * AMS `platform_roles` claim. The SPI omits it entirely for customers and
   * injects it only for Monarch staff, so its presence is the audience signal.
   */
  platformRoles?: string[];
  /** Keycloak `email` claim: a profile trait, never an event property. */
  email?: string;
  /** Keycloak `given_name` claim: a profile trait. */
  firstName?: string;
  /** Keycloak `family_name` claim: a profile trait. */
  lastName?: string;
  /** AMS `account_slug` claim; null or absent for a user with no membership. */
  account_slug?: string | null;
  /** AMS `organization_name` claim; dropped silently when the token lacks it. */
  organization_name?: string;
}

/**
 * `internal` when the token carries any platform-scope role, else `customer`.
 * Defaults to `customer` whenever the signal is absent or undeterminable.
 */
export const deriveAudience = (platformRoles?: string[]): AnalyticsAudience =>
  platformRoles && platformRoles.length > 0 ? 'internal' : 'customer';

/**
 * Last applied identity, as a signature over every field the SDK calls carry
 * (sub, account, plan, audience, email, names, slug, organization). The auth
 * layer re-applies identity on every token refresh; this keeps the SDK calls
 * to the transitions that actually change something.
 */
let lastIdentitySignature: string | null = null;

/**
 * Last identity handed to `applyAnalyticsIdentity`, kept even while opted out
 * so an in-session opt-in can re-identify without an auth refresh. Cleared on
 * sign-out.
 */
let lastIdentity: AnalyticsIdentity | null = null;

/**
 * Attach the Keycloak subject as the analytics profile and set the five
 * standard global properties (`app`, `environment`, `account_id`, `plan`,
 * `audience`). Global properties merge into every subsequent event, so no
 * individual call site has to pass them.
 *
 * The identify call carries the readable traits (task 1296): email, first and
 * last name as the SDK's own profile fields, and `account_slug` /
 * `organization_name` as profile properties. After identify, the profile and
 * every later event are linked to the `account` group, whose id is the AMS
 * account id (the group itself is upserted server-side by AMS).
 *
 * Safe to call on every auth state change — repeated calls with an unchanged
 * identity are no-ops, while a token refresh that changes plan or account
 * re-applies immediately.
 */
export function applyAnalyticsIdentity(identity: AnalyticsIdentity): void {
  if (!identity.profileId) return;
  // Remembered even while the consent gate is closed, so opting back in can
  // re-apply it immediately.
  lastIdentity = identity;
  if (!isAnalyticsEnabled()) return;

  const audience = deriveAudience(identity.platformRoles);
  const accountId = identity.accountId ?? '';
  const plan = identity.plan ?? '';

  // Empty strings (AuthUser's default for a missing claim) are absent traits.
  const email = identity.email || undefined;
  const firstName = identity.firstName || undefined;
  const lastName = identity.lastName || undefined;
  const accountSlug = identity.account_slug || undefined;
  const organizationName = identity.organization_name || undefined;

  const signature = [
    identity.profileId,
    accountId,
    plan,
    audience,
    email ?? '',
    firstName ?? '',
    lastName ?? '',
    accountSlug ?? '',
    organizationName ?? '',
  ].join('|');
  if (signature === lastIdentitySignature) return;
  lastIdentitySignature = signature;

  op.setGlobalProperties({
    app: APP_NAME,
    environment: config.ENVIRONMENT,
    account_id: accountId,
    plan,
    audience,
  });

  // Passing properties (rather than profileId alone) is what makes the SDK
  // emit an identify call, so the profile materialises in OpenPanel instead of
  // only being attached locally. Email and name go in the SDK's own profile
  // fields so the dashboard shows them; they never reach `op.global`.
  void op.identify({
    profileId: identity.profileId,
    ...(email ? { email } : {}),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    properties: {
      account_id: accountId,
      plan,
      audience,
      ...(accountSlug ? { account_slug: accountSlug } : {}),
      ...(organizationName ? { organization_name: organizationName } : {}),
    },
  });

  // Link the profile and every later event to the account group. The SDK keeps
  // a list of groups and appends to it, so it is reset first: after an account
  // switch, events belong to the new account only. Behind the same consent
  // gate as identify (the early return above, plus the SDK filter).
  op.groups = [];
  if (identity.accountId) void op.setGroup(identity.accountId);
}

/**
 * Drop the analytics identity on sign-out so the next session does not inherit
 * the previous user's profile or account properties. `op.clear()` resets the
 * profile/device/session but leaves global properties in place, so those are
 * cleared explicitly.
 */
export function resetAnalyticsIdentity(): void {
  lastIdentitySignature = null;
  lastIdentity = null;
  if (!isAnalyticsEnabled()) return;
  op.clear();
  op.global = {};
}

// ─── Product events (task 427) ────────────────────────────────────────────────

/**
 * Emit a named product event. The single emission seam for the app — every
 * call site goes through here rather than touching `op` directly, so enabling,
 * suppression, and future consent gating stay in one place.
 *
 * Event names are snake_case past tense; properties must never contain PII.
 */
export function trackEvent(name: string, properties?: TrackProperties): void {
  if (!isAnalyticsEnabled()) return;
  void op.track(name, properties);
}

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * The current route as a low-cardinality tag for the `location` property —
 * the pathname with UUID segments collapsed to `:id`, e.g. `/endpoints/:id`.
 * Query strings are dropped: they can carry user-typed values.
 */
export function currentLocationTag(): string {
  if (typeof window === 'undefined') return 'unknown';
  return window.location.pathname.replace(UUID_PATTERN, ':id') || '/';
}

/**
 * Normalise a short developer-authored label (a toast title, an error kind)
 * into a stable snake_case slug for grouping in OpenPanel. Truncated so an
 * unexpectedly long or interpolated string can never become a payload of its
 * own — never pass a raw error message or any user-entered text through here.
 */
export function toEventSlug(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 48) || 'unknown'
  );
}
